import { applyParams, downloadFile, DownloadProgressCallback, FileContainer, process } from "am-tax-fe-core";

import { engagementApi, getEngagementRegion, getProjectRegion, projectApi } from "../api";
import { components } from "../generated/generated-api";
import { uploadFile } from "../upload-utils";

const ENG_FORM_DEF_PATH = "/api/DynamicForm/engagement/{engagementId}/dynamicformdefinitions";
const PRJ_FORM_DEF_PATH = "/api/DynamicForm/engagement/{engagementId}/project/{projectId}/dynamicformdefinitions";
export type DynamicFormDefinitionResponse = components["schemas"]["DynamicFormDefinitionResponse"];

const ENG_FORM_DATA_PATH = "/api/DynamicForm/engagement/{engagementId}/dynamicFormDefinition/{dynamicFormDefinitionId}/dynamicforms";
const PRJ_FORM_DATA_PATH = "/api/DynamicForm/engagement/{engagementId}/project/{projectId}/dynamicFormDefinition/{dynamicFormDefinitionId}/dynamicforms";
export type DynamicFormResponse = components["schemas"]["DynamicFormResponse"];

const ENG_FORM_DATA_CREATE_PATH = "/api/DynamicForm/engagement/{engagementId}/dynamicFormDefinition/{dynamicFormDefinitionId}/dynamicform";
const PRJ_FORM_DATA_CREATE_PATH = "/api/DynamicForm/engagement/{engagementId}/project/{projectId}/dynamicFormDefinition/{dynamicFormDefinitionId}/dynamicform";

const ENG_FORM_DATA_UPDATE_PATH = "/api/DynamicForm/engagement/{engagementId}/dynamicForm/{dynamicFormId}/data";
const PRJ_FORM_DATA_UPDATE_PATH = "/api/DynamicForm/engagement/{engagementId}/project/{projectId}/dynamicForm/{dynamicFormId}/data";

const ENG_FORM_UPDATE_DELETE_PATH = "/api/DynamicForm/engagement/{engagementId}/dynamicForm/{dynamicFormId}/form";
const PRJ_FORM_UPDATE_DELETE_PATH = "/api/DynamicForm/engagement/{engagementId}/project/{projectId}/dynamicForm/{dynamicFormId}/form";
export type DynamicFormUpdateRequest = components["schemas"]["DynamicFormUpdateRequest"];

// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
const ENG_FORM_DATA_DOWNLOAD_PATH = "/api/DynamicForm/engagement/{engagementId}/dynamicforms/content";
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
const PRJ_FORM_DATA_DOWNLOAD_PATH = "/api/DynamicForm/engagement/{engagementId}/project/{projectId}/dynamicforms/content";

const ENG_FORM_DATA_DOWNLOAD_POST_PATH = "/api/DynamicForm/engagement/{engagementId}/dynamicformsbyid/content";
const PRJ_FORM_DATA_DOWNLOAD_POST_PATH = "/api/DynamicForm/engagement/{engagementId}/project/{projectId}/dynamicformsbyid/content";

const defaultBaseUrl = "";
export const getEngagementFormDefinitions = async (engagementId: string): Promise<DynamicFormDefinitionResponse[]> =>
    engagementApi({ engagementId }, client => process(client.GET(ENG_FORM_DEF_PATH, { params: { path: { engagementId } } })));

export const getProjectFormDefinitions = async (engagementId: string, projectId: string): Promise<DynamicFormDefinitionResponse[]> =>
    projectApi({ engagementId, projectId }, client => process(client.GET(PRJ_FORM_DEF_PATH, { params: { path: { engagementId, projectId } } })));

export const getEngagementFormData = async (engagementId: string, dynamicFormDefinitionId: string): Promise<DynamicFormResponse> =>
    engagementApi({ engagementId }, client => process(client.GET(ENG_FORM_DATA_PATH, { params: { path: { engagementId, dynamicFormDefinitionId } } })));

export const getProjectFormData = async (engagementId: string, projectId: string, dynamicFormDefinitionId: string): Promise<DynamicFormResponse> =>
    projectApi({ engagementId, projectId }, client =>
        process(client.GET(PRJ_FORM_DATA_PATH, { params: { path: { engagementId, projectId, dynamicFormDefinitionId } } })),
    );

export type CreateDynamicFormArgs = { engagementId: string; projectId?: string; dynamicFormDefinitionId: string; data: string; isDraft: boolean };
export type UpdateDynamicFormArgs = { engagementId: string; projectId?: string; dynamicFormId: string; data: string; isDraft: boolean };

export const createEngagementDynamicForm = async ({
    engagementId,
    dynamicFormDefinitionId,
    data,
    isDraft,
}: CreateDynamicFormArgs): Promise<DynamicFormResponse> =>
    engagementApi({ engagementId }, client =>
        process(
            client.POST(ENG_FORM_DATA_CREATE_PATH, {
                params: { path: { engagementId, dynamicFormDefinitionId } },
                body: { data, isDraft },
            }),
        ),
    );

export const createProjectDynamicForm = async ({
    engagementId,
    projectId,
    dynamicFormDefinitionId,
    data,
    isDraft,
}: CreateDynamicFormArgs): Promise<DynamicFormResponse> => {
    return projectApi({ engagementId, projectId: projectId || "" }, client =>
        process(
            client.POST(PRJ_FORM_DATA_CREATE_PATH, {
                params: { path: { engagementId, projectId: projectId || "", dynamicFormDefinitionId } },
                body: { data, isDraft },
            }),
        ),
    );
};

export type DownloadEngagementFormDefinitionsArgs = { engagementId: string; definitionIds: string[] } & { progressCallback: DownloadProgressCallback };
export const downloadEngagementDynamicForms = async (args: DownloadEngagementFormDefinitionsArgs): Promise<Blob> => {
    const region = await getEngagementRegion({ engagementId: args.engagementId });
    const response = await downloadFile(
        region?.endpointUrl || "",
        applyParams(ENG_FORM_DATA_DOWNLOAD_POST_PATH, { engagementId: args.engagementId }),
        args.progressCallback,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ formDefinitionIds: args.definitionIds }),
        },
    );
    if (response.ok) {
        return response.blob();
    } else {
        throw new Error("The file could not be downloaded.");
    }
};

export type DownloadProjectFormDefinitionsArgs = { engagementId: string; projectId: string; definitionIds: string[] } & {
    progressCallback: DownloadProgressCallback;
};
export const downloadProjectDynamicForms = async (args: DownloadProjectFormDefinitionsArgs): Promise<Blob> => {
    const region = await getProjectRegion({ engagementId: args.engagementId, projectId: args.projectId });
    const response = await downloadFile(
        region?.endpointUrl || "",
        applyParams(PRJ_FORM_DATA_DOWNLOAD_POST_PATH, { engagementId: args.engagementId, projectId: args.projectId }),
        args.progressCallback,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ formDefinitionIds: args.definitionIds }),
        },
    );
    if (response.ok) {
        return response.blob();
    } else {
        throw new Error("The file could not be downloaded.");
    }
};

// Dynamic Forms
export const getDynamicFormTemplate = async (
    engagementId: string,
    projectId: string | undefined,
    progressCallback: DownloadProgressCallback,
): Promise<Blob> => {
    let region;
    let url;
    if (projectId != undefined) {
        region = await getProjectRegion({ engagementId, projectId });
        url = `/api/DynamicForm/engagement/${engagementId}/project/${projectId}/template`;
    } else {
        region = await getEngagementRegion({ engagementId });
        url = `/api/DynamicForm/engagement/${engagementId}/template`;
    }
    const response = await downloadFile(region?.endpointUrl || defaultBaseUrl, url, progressCallback);
    if (response.ok) {
        return response.blob();
    } else {
        throw new Error("The dynamic form template could not be downloaded");
    }
};

export type CustomDynamicFormRequest = components["schemas"]["CustomDynamicFormUploadRequest"];
export type CustomDynamicFormResponse = components["schemas"]["CustomDynamicFormResponse"];
const createCustomDynamicFormEngagementPath = "/api/DynamicForm/engagement/{engagementId}/custom";
const createCustomDynamicFormProjectPath = "/api/DynamicForm/engagement/{engagementId}/project/{projectId}/custom";
export const createCustomDynamicForm = async (
    engagementId: string,
    projectId: string | undefined,
    data: CustomDynamicFormRequest,
): Promise<CustomDynamicFormResponse> => {
    if (projectId != undefined) {
        return projectApi({ engagementId, projectId }, client =>
            process(client.POST(createCustomDynamicFormProjectPath, { params: { path: { engagementId, projectId } }, body: data })),
        );
    } else {
        return engagementApi({ engagementId }, client =>
            process(client.POST(createCustomDynamicFormEngagementPath, { params: { path: { engagementId } }, body: data })),
        );
    }
};

const customDynamicFormUploadEngagementPath = "/api/DynamicForm/engagement/{engagementId}/custom/{customFormId}/upload";
const customDynamicFormUploadProjectPath = "/api/DynamicForm/engagement/{engagementId}/project/{projectId}/custom/{customFormId}/upload";
export const uploadCustomDynamicFormFile = async (
    engagementId: string,
    projectId: string | undefined,
    customFormId: string,
    fileContainer: FileContainer,
): Promise<void> => {
    if (projectId != undefined) {
        const region = await getProjectRegion({ engagementId, projectId });
        return uploadFile(
            region?.endpointUrl || defaultBaseUrl,
            applyParams(customDynamicFormUploadProjectPath, { engagementId, projectId, customFormId }),
            fileContainer,
        );
    } else {
        const region = await getEngagementRegion({ engagementId });
        return uploadFile(
            region?.endpointUrl || defaultBaseUrl,
            applyParams(customDynamicFormUploadEngagementPath, { engagementId, customFormId }),
            fileContainer,
        );
    }
};

export const updateDynamicForm = async (
    engagementId: string,
    projectId: string | undefined,
    dynamicFormId: string,
    data: DynamicFormUpdateRequest,
): Promise<DynamicFormDefinitionResponse> => {
    if (projectId != undefined) {
        return projectApi({ engagementId, projectId }, client =>
            process(client.PATCH(PRJ_FORM_UPDATE_DELETE_PATH, { params: { path: { engagementId, projectId, dynamicFormId } }, body: data })),
        );
    } else {
        return engagementApi({ engagementId }, client =>
            process(client.PATCH(ENG_FORM_UPDATE_DELETE_PATH, { params: { path: { engagementId, dynamicFormId } }, body: data })),
        );
    }
};

export const updateEngagementDynamicFormData = async ({ engagementId, dynamicFormId, data, isDraft }: UpdateDynamicFormArgs): Promise<DynamicFormResponse> => {
    return engagementApi({ engagementId }, client =>
        process(
            client.PUT(ENG_FORM_DATA_UPDATE_PATH, {
                params: { path: { engagementId, dynamicFormId } },
                body: { data, isDraft },
            }),
        ),
    );
};

export const updateProjectDynamicFormData = async ({
    engagementId,
    projectId,
    dynamicFormId,
    data,
    isDraft,
}: UpdateDynamicFormArgs): Promise<DynamicFormResponse> =>
    projectApi({ engagementId, projectId: projectId || "" }, client =>
        process(
            client.PUT(PRJ_FORM_DATA_UPDATE_PATH, {
                params: { path: { engagementId, projectId: projectId || "", dynamicFormId } },
                body: { data, isDraft },
            }),
        ),
    );

export const deleteDynamicForm = async (engagementId: string, projectId: string | undefined, dynamicFormId: string): Promise<unknown> => {
    if (projectId != undefined) {
        return projectApi({ engagementId, projectId }, client =>
            process(client.DELETE(PRJ_FORM_UPDATE_DELETE_PATH, { params: { path: { engagementId, projectId, dynamicFormId } } })),
        );
    } else {
        return engagementApi({ engagementId }, client =>
            process(client.DELETE(ENG_FORM_UPDATE_DELETE_PATH, { params: { path: { engagementId, dynamicFormId } } })),
        );
    }
};
